import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { rechtFehlerOder } from '../../../services/rechte/rechtFehler';
import { AUTO_SPEICHER_MS, SpeicherAnzeige, SpeicherPhase, speicherAnzeige } from './gbuModulLogic';
import { type SpeicherLauf, erzeugeSpeicherLauf } from './speicherLauf';
import { SpeicherKontext, useUngespeichert } from './useUngespeichert';

export interface AutoSpeichern {
  anzeige: SpeicherAnzeige;
  /** Grund des letzten Fehlschlags (Klartext). */
  fehler: string | null;
  /** Sofort speichern und warten; true = danach nichts mehr offen. */
  flush: () => Promise<boolean>;
  /** Nur auf einen laufenden Lauf warten, ohne einen neuen zu starten (Verwerfen, Loeschen). */
  warte: () => Promise<void>;
  erneut: () => void;
}

interface Optionen {
  /** Der Entwurf weicht vom gespeicherten Stand ab (bei neuen Zeilen: hat Eingaben). */
  geaendert: boolean;
  /** Darf jetzt gespeichert werden (bearbeitbar und gueltig)? */
  bereit: boolean;
  /** Fingerabdruck des Entwurfs — jede Aenderung startet die Entprellung neu. */
  schluessel: string;
  /**
   * Schreibt den AKTUELLEN Entwurf. Muss selbst pruefen, ob gegenueber dem
   * zuletzt GESPEICHERTEN Stand (eigene Ref, nach Erfolg sofort gesetzt)
   * ueberhaupt etwas zu tun ist — ein nachgezogener Lauf darf nie doppelt
   * schreiben oder eine Zeile ein zweites Mal anlegen.
   */
  speichere: () => Promise<void>;
  /**
   * Muss jetzt geschrieben werden? Neuester Entwurf gegen den zuletzt
   * BESTAETIGTEN Stand (Ref, nach jeder Antwort sofort gesetzt) — anders als
   * `geaendert` nie veraltet. Die Warteschlange schreibt nach, solange das
   * zutrifft (speicherLauf.ts).
   */
  zuTun: () => boolean;
  /** An den Editor melden (Standard: ja). Der Kopf meldet selbst. */
  melden?: boolean;
}

/**
 * Automatisches Speichern einer Einheit (Kopf, Gefaehrdung, Massnahme,
 * Vorsorge): entprellt nach der letzten Eingabe, sofort per `flush`
 * (Verlassen des Feldes, Zuklappen, „Zur Freigabe", Zurueck).
 *
 * Die Warteschlange (nie zwei Laeufe gleichzeitig, der neueste Stand gewinnt)
 * steckt in speicherLauf.ts und ist dort ohne React geprueft; dieser Hook
 * haengt nur Zeitgeber, Anzeige und Editor daran.
 *
 * Nichts geht beim Verlassen verloren: wird die Einheit abgebaut, solange die
 * Entprellung noch laeuft, wird trotzdem gespeichert; ebenso beim Verlassen
 * bzw. Verstecken der Seite (pagehide, visibilitychange).
 *
 * Zustand wird nur in Zeitgeber- und Promise-Rueckrufen gesetzt, nie im
 * Effekt-Koerper; die Anzeige ergibt sich rein aus Phase und Entwurf
 * (gbuModulLogic.speicherAnzeige).
 */
export const useAutoSpeichern = ({ geaendert, bereit, schluessel, speichere, zuTun, melden = true }: Optionen): AutoSpeichern => {
  const [phase, setPhase] = useState<SpeicherPhase>('ruhig');
  const [fehler, setFehler] = useState<string | null>(null);
  const speichereRef = useRef(speichere);
  const zuTunRef = useRef(zuTun);
  const standRef = useRef({ geaendert, bereit, generation: schluessel });
  const timerRef = useRef<number | null>(null);
  const lauferRef = useRef<SpeicherLauf | null>(null);

  // Gleich nach dem Festschreiben (vor jedem Zeitgeber und jeder Antwort): der neueste Entwurf, sein
  // Zustand und seine Generation (Fingerabdruck) — die Warteschlange erkennt daran Eingaben waehrend
  // des Schreibens und schreibt dann nach.
  useLayoutEffect(() => {
    speichereRef.current = speichere;
    zuTunRef.current = zuTun;
    standRef.current = { geaendert, bereit, generation: schluessel };
  });

  /** Die Warteschlange dieser Einheit — einmal, beim ersten Gebrauch (nie waehrend des Zeichnens). */
  const laufer = useCallback((): SpeicherLauf => {
    if (!lauferRef.current) {
      lauferRef.current = erzeugeSpeicherLauf({
        stand: () => standRef.current,
        zuTun: () => zuTunRef.current(),
        speichere: () => speichereRef.current(),
        onPhase: (p, f) => { setPhase(p); setFehler(f); },
        fehlerText: rechtFehlerOder,
      });
    }
    return lauferRef.current;
  }, []);

  const stoppeTimer = useCallback(() => {
    if (timerRef.current === null) return;
    window.clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  /** Jetzt speichern: eine laufende Entprellung ist damit erledigt. */
  const flush = useCallback((): Promise<boolean> => {
    stoppeTimer();
    return laufer().lauf();
  }, [laufer, stoppeTimer]);

  const warte = useCallback((): Promise<void> => laufer().warte(), [laufer]);

  // Entprellung: jede Aenderung des Entwurfs startet die Wartezeit neu.
  useEffect(() => {
    stoppeTimer();
    if (geaendert && bereit) {
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        void laufer().lauf();
      }, AUTO_SPEICHER_MS);
    }
  }, [schluessel, geaendert, bereit, laufer, stoppeTimer]);

  // Abbau mit laufender Entprellung (Karte entfernt, Editor verlassen): trotzdem speichern.
  useEffect(() => () => {
    if (timerRef.current === null) return;
    stoppeTimer();
    void laufer().lauf();
  }, [laufer, stoppeTimer]);

  // Seite wird verlassen oder versteckt (Tab zu, App im Hintergrund): Offenes sofort speichern.
  useEffect(() => {
    const weg = () => {
      const s = standRef.current;
      if (s.geaendert && s.bereit) void flush();
    };
    const versteckt = () => { if (document.visibilityState === 'hidden') weg(); };
    window.addEventListener('pagehide', weg);
    document.addEventListener('visibilitychange', versteckt);
    return () => {
      window.removeEventListener('pagehide', weg);
      document.removeEventListener('visibilitychange', versteckt);
    };
  }, [flush]);

  const kontext = useContext(SpeicherKontext);
  useEffect(() => (kontext ? kontext.registriere(flush) : undefined), [kontext, flush]);
  useUngespeichert(melden && geaendert);

  const erneut = useCallback(() => { void flush(); }, [flush]);
  return { anzeige: speicherAnzeige(phase, geaendert, bereit), fehler, flush, warte, erneut };
};
